import { Menu, Box, Group, LoadingOverlay, Badge, Text } from '@mantine/core';
import { IconDotsVertical, IconDownload, IconTrashX } from '@tabler/icons-react';
import { useFileManager } from 'api/file-manager/file-manager-context';
import P2RegularLink from 'components/typography/P2RegularLink';
import { useCallback, useEffect, useState } from 'react';
import useFileDownload from 'api/use-file-download';
import FileThumbnail from 'components/files/FileThumbnail';
import Excerpt from 'components/Excerpt';
import { noop } from 'lodash';
import { useConfirm } from 'components/modals/message/MessageProvider';
import ActionIcon, { type AppActionIconSize } from 'components/ActionIcon';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

export type FilePreviewSize = 'sm' | 'md' | 'lg';

const FILE_PREVIEW_SIZE_CONFIG: Record<
  FilePreviewSize,
  {
    height: number;
    contentPadding: number;
    desktopActionIconSize: AppActionIconSize;
    mobileActionIconSize: AppActionIconSize;
  }
> = {
  sm: { height: 56, contentPadding: 12, desktopActionIconSize: 'sm', mobileActionIconSize: 'sm' },
  md: { height: 80, contentPadding: 20, desktopActionIconSize: 'md', mobileActionIconSize: 'sm' },
  lg: { height: 104, contentPadding: 24, desktopActionIconSize: 'lg', mobileActionIconSize: 'md' },
};

/**
 * The parameters of the FilePreview component.
 */
export interface FilePreviewProps {
  fileId: string;
  size?: FilePreviewSize;
  label?: string;
  onFileDelete?: () => void;
  showSlider?: boolean;
  onOpenSlider?: () => void;
  hideDownload?: boolean;
  hideDelete?: boolean;
  excerptLength?: number;
  withKep?: boolean;
}

/**
 * The file preview component.
 */
export default function FilePreview({
  fileId,
  size = 'md',
  label,
  onFileDelete = noop,
  showSlider = true,
  onOpenSlider = noop,
  hideDownload = false,
  hideDelete = false,
  excerptLength = 50,
  withKep = false,
}: FilePreviewProps) {
  const { confirm } = useConfirm();
  const [fileName, setFileName] = useState('');
  const [fileExtension, setFileExtension] = useState('');
  const [canOpenSlider, setCanOpenSlider] = useState(false);
  const { getFileMetadata } = useFileManager();
  const { loading, download } = useFileDownload();
  const { isMobile } = useResponsiveDesign();
  const { height, contentPadding, desktopActionIconSize, mobileActionIconSize } = FILE_PREVIEW_SIZE_CONFIG[size];

  /**
   * Confirms the file deletion.
   */
  const handleDelete = useCallback(
    () =>
      confirm({
        title: 'Zmazať súbor',
        content: 'Naozaj chcete zmazať súbor?',
        onConfirm: onFileDelete,
      }),
    [onFileDelete]
  );

  // Fetch the file metadata.
  useEffect(() => {
    getFileMetadata({ fileId }).then((metadata) => {
      setFileName(metadata.name);
      setFileExtension(metadata.extension);
      setCanOpenSlider(metadata.mimeType.startsWith('image/') || metadata.mimeType === 'application/pdf');
    });
  }, [fileId]);

  /**
   * Opens a preview for previewable files and downloads the rest.
   */
  const handleOpen = useCallback(() => {
    if (showSlider && canOpenSlider) {
      onOpenSlider();
    } else {
      download(fileId);
    }
  }, [canOpenSlider, download, fileId, onOpenSlider, showSlider]);

  return (
    <Box
      pos="relative"
      bg="gray.0"
      p={0}
      m={0}
      w="full"
      role="group"
      aria-label="Náhľad súboru"
      data-size={size}
      style={{
        display: 'grid',
        gridTemplateColumns: `${height}px 1fr`,
        height,
        borderRadius: '4px',
      }}
    >
      <LoadingOverlay visible={loading} loaderProps={{ size: 'sm' }} />
      <FileThumbnail
        fileId={fileId}
        size={size}
        showSlider={showSlider}
        onOpenSlider={onOpenSlider}
        onUnsupportedPreviewClick={() => download(fileId)}
      />
      <Group position="apart" p={contentPadding} noWrap>
        <Group p={4} m={-4} spacing={8} style={{ cursor: 'pointer' }} onClick={handleOpen}>
          <P2RegularLink color="gray.7">
            <Excerpt text={fileName} suffix={fileExtension} length={excerptLength} />
          </P2RegularLink>
          {withKep && (
            <Badge color="green" size="sm" variant="filled">
              KEP
            </Badge>
          )}
        </Group>

        <Menu shadow="md" width={160} position="bottom-end">
          <Menu.Target>
            <ActionIcon
              variant="subtle-gray"
              size={isMobile ? mobileActionIconSize : desktopActionIconSize}
              aria-label="Akcie súboru"
            >
              <IconDotsVertical stroke="1.5" />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {label && (
              <Menu.Label>
                <Text maw={160} truncate title={label}>
                  {label}
                </Text>
              </Menu.Label>
            )}
            {!hideDownload && (
              <Menu.Item color="gray.8" icon={<IconDownload stroke={1.5} size={20} />} onClick={() => download(fileId)}>
                Stiahnúť
              </Menu.Item>
            )}
            {!hideDelete && (
              <Menu.Item color="red.8" icon={<IconTrashX stroke={1.5} size={20} />} onClick={handleDelete}>
                Zmazať súbor
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Box>
  );
}
